import { defineComponent, PropType } from 'vue';
import cancel from '@/shared/icons/cancel/cancel.vue';
import iconRemoveTag from '@/shared/icons/removeTag/removeTag.vue';
import hadrianLabel from '@/shared/viewParts/hadrianLabel/hadrianLabel.vue';
import hadrianLoader from '@/shared/viewParts/hadrianLoader/hadrianLoader.vue';
import { SearchMode } from '@/shared/viewParts/filteredSelector/filteredSelector';

export default defineComponent({
  name: 'tagBox',
  components: {
    cancel,
    hadrianLabel,
    iconRemoveTag,
    hadrianLoader,
  },
  emits: ['input-values'],
  props: {
    suggestionList: {
      type: Array as PropType<string[] | { label: string; value: string }[]>,
      required: true,
    },
    allowCustom: {
      type: Boolean,
      default: false,
    },
    searchMode: {
      type: String as PropType<SearchMode>,
      default: SearchMode.Includes,
    },
    placeholder: {
      type: String,
      default: '',
    },

    label: { type: String },
    helpTooltip: { type: String },
    mandatory: { type: Boolean },
    maxItemCount: { type: Number },
    maxItemCountLabel: { type: String },
    validator: {
      type: Function as PropType<(value: string) => boolean>,
      default: () => true,
    },
    errorMessageKey: { type: String },
    showLoading: {
      type: Boolean,
      default: false,
    },
    message: { type: String },
    recipientMessage: { type: String },
    initialValues: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  created() {
    if (this.initialValues?.length) {
      this.selectedValues = [...this.initialValues];
    }
  },
  data() {
    return {
      input: '',
      showSuggestions: false,
      selectedValues: [] as string[],
      hasChangeHappened: false,
      hasFocusHappened: false,
    };
  },
  computed: {
    normalizedSuggestions(): { label: string; value: string }[] {
      if (!this.suggestionList || !Array.isArray(this.suggestionList)) {
        return [];
      }

      if (this.suggestionList.length === 0) {
        return [];
      }

      if (typeof this.suggestionList[0] === 'string') {
        return (this.suggestionList as string[]).map(item => ({
          label: item,
          value: item,
        }));
      }

      return this.suggestionList as { label: string; value: string }[];
    },

    filteredSuggestions(): { label: string; value: string }[] {
      const term = this.input.trim().toLowerCase();

      return this.normalizedSuggestions.filter(item => {
        const label = (item.label || '').toLowerCase();
        const matches =
          term === ''
            ? true
            : this.searchMode === SearchMode.StartsWith
            ? label.startsWith(term)
            : label.includes(term);

        return matches && !this.selectedValues.includes(item.value);
      });
    },
    instructions(): string {
      return [
        this.maxItemCountLabel ?? this.$t('tagBox.instructions'),
        this.maxItemCount ? this.$t('tagBox.maxItemCountLabel', { count: this.maxItemCount }) : null,
      ]
        .filter(Boolean)
        .join(' ');
    },
    isMaxItemCountReached(): boolean {
      if (this.maxItemCount == null) {
        return false;
      }
      return this.selectedValues.length >= this.maxItemCount;
    },
    invalidItemCount(): number {
      if (this.validator == null) {
        return 0;
      }
      return this.selectedValues.filter(v => !this.validator(v)).length;
    },
    isValid(): boolean {
      if (!this.hasChangeHappened && !this.hasFocusHappened) {
        return true;
      }
      if (this.selectedValues.length === 0 && this.mandatory) {
        return false;
      }
      if (this.invalidItemCount > 0) {
        return false;
      }
      return true;
    },
    errorMessage(): string | null {
      if (!this.errorMessageKey || this.invalidItemCount === 0) {
        return null;
      }
      return this.$tc(this.errorMessageKey, this.invalidItemCount);
    },
  },
  methods: {
    getLabel(val: string): string {
      const found = this.normalizedSuggestions.find(i => i.value === val);
      return found ? found.label : val;
    },

    selectSuggestion(item: { label: string; value: string }) {
      if (!item || this.isMaxItemCountReached) return;

      if (!this.selectedValues.includes(item.value)) {
        this.selectedValues.push(item.value);
        this.$emit('input-values', [...this.selectedValues]);
      }

      this.input = '';
      this.showSuggestions = false;
    },

    focusInput() {
      const inputEl = this.$refs.input as HTMLInputElement;
      if (inputEl) {
        inputEl.focus();
        this.showSuggestions = true;
      }
    },

    onFocus() {
      this.showSuggestions = true;
      if (this.selectedValues.length > 0) {
        this.hasFocusHappened = true;
      }
    },

    onInput(event: Event) {
      this.showSuggestions = true;
      this.hasChangeHappened = true;

      const input = (event.target as HTMLInputElement).value;
      this.input = input;

      const lastCharacter = input[input.length - 1];
      if (lastCharacter !== ',') {
        return;
      }

      if (this.isMaxItemCountReached) {
        this.input = this.input.slice(0, this.input.length - 1);
        return;
      }

      const value = input.slice(0, input.length - 1).trim();
      if (!value) {
        this.input = '';
        return;
      }

      this.addValueIfValid(value);
      this.input = '';
    },

    addValueIfValid(value: string) {
      if (this.isMaxItemCountReached) {
        return;
      }

      if (this.selectedValues.includes(value)) {
        return;
      }

      if (this.validator && !this.validator(value)) {
        if (this.allowCustom) {
          this.selectedValues.push(value);
          this.$emit('input-values', [...this.selectedValues]);
        }
        return;
      }

      this.selectedValues.push(value);
      this.$emit('input-values', [...this.selectedValues]);
    },

    addCustomTag() {
      const val = this.input.trim();
      if (!val || this.isMaxItemCountReached) return;

      if (this.allowCustom) {
        this.addValueIfValid(val);
      }

      this.input = '';
      this.showSuggestions = false;
    },

    onKeyPress() {
      this.hasChangeHappened = false;
    },

    onDeleteKeyPress(event: KeyboardEvent) {
      if (this.hasChangeHappened || event.key === 'Delete') {
        return;
      }

      if (!this.input && this.selectedValues.length > 0) {
        const lastValue = this.selectedValues.pop();
        this.$emit('input-values', [...this.selectedValues]);
        if (lastValue) {
          this.input = lastValue;
        }
      }
    },

    removeTag(idx: number) {
      this.selectedValues.splice(idx, 1);
      this.$emit('input-values', [...this.selectedValues]);
    },

    onBlur() {
      if (!this.input || this.isMaxItemCountReached) {
        return;
      }

      const trimmedInput = this.input.trim();
      if (this.allowCustom && trimmedInput) {
        this.addValueIfValid(trimmedInput);
      }
      this.input = '';
      this.showSuggestions = false;
    },

    hideSuggestions() {
      this.showSuggestions = false;
    },
  },
});
